import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Box, Column, extractPureTextFromRichText, RichTextViewer, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { buildSystemVariableResolution, templateVariablesTextsPath } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariables.utils';
import { VariableInlineRenderer } from '~/components/AgreementStudioPage/TemplateVariables/VariableInlineRenderer/VariableInlineRenderer';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { AgreementDataModel, relationshipModel } from '~/models';
import { costDisplayTextsPath } from '~/models/service-cost/selectors';
import { toDate } from '~/models/time/translator';
import { billingPeriodTextsPath } from '~/utils/billing-period-text';
import { getServiceBulletPoints } from '~/utils/service-description/getServiceBulletPoints';
import styles from './ServiceDescription.module.scss';
export const GetServiceDescriptionTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export const ServiceDescription = (param)=>{
    let { service, relationship, now, hideIncludedServices = false, resolvedValues: providedResolvedValues, includedServiceDescriptionsTestId, billingDetailsTestId, testId } = param;
    var _ref;
    var _service_bundle;
    const { ServiceDescriptionTestIds: testIds } = useMemo(()=>GetServiceDescriptionTID(testId), [
        testId
    ]);
    const featureFlags = useFeatureFlags();
    const { t: billingTexts } = useLocaleKeys(billingPeriodTextsPath);
    const { t: costDisplayTexts } = useLocaleKeys(costDisplayTextsPath);
    const { t: reviewTexts } = useLocaleKeys('clientFlow.reviewServices');
    const { t: templateVarTexts } = useLocaleKeys(templateVariablesTextsPath);
    const agreementData = useMemo(()=>AgreementDataModel.fromRelationship(relationship), [
        relationship
    ]);
    const resolvedValues = useMemo(()=>{
        var _relationship_buyer_firstName, _relationship_buyer_lastName;
        return providedResolvedValues !== null && providedResolvedValues !== void 0 ? providedResolvedValues : buildSystemVariableResolution({
            clientCompanyName: relationshipModel.customerName(relationship),
            clientFirstName: (_relationship_buyer_firstName = relationship.buyer.firstName) !== null && _relationship_buyer_firstName !== void 0 ? _relationship_buyer_firstName : undefined,
            clientLastName: (_relationship_buyer_lastName = relationship.buyer.lastName) !== null && _relationship_buyer_lastName !== void 0 ? _relationship_buyer_lastName : undefined,
            clientEmail: relationship.buyer.email,
            vendorBusinessName: relationship.vendor.name,
            agreementName: relationship.title,
            effectiveDate: agreementData.effectiveDate ? toDate(agreementData.effectiveDate) : null,
            currentDate: now
        }, templateVarTexts);
    }, [
        providedResolvedValues,
        relationship,
        agreementData.effectiveDate,
        now
    ]);
    const bulletPoints = getServiceBulletPoints({
        service,
        now,
        agreementData,
        isReadOnly: true,
        skin: 'standard',
        variant: 'paragraph2',
        weight: 'light',
        featureFlags,
        billingTexts,
        costDisplayTexts
    });
    const hasDescription = service.description != null && extractPureTextFromRichText(service.description).trim().length > 0;
    const includedServices = (_ref = (_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services) !== null && _ref !== void 0 ? _ref : [];
    const hasIncludedServices = includedServices.length > 0;
    const includedWithDescription = includedServices.filter((s)=>s.description != null && extractPureTextFromRichText(s.description).trim().length > 0);
    return /*#__PURE__*/ _jsxs(Column, {
        gap: 16,
        children: [
            hasDescription && /*#__PURE__*/ _jsxs(Column, {
                gap: 4,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        weight: "bold",
                        skin: "standard",
                        testId: testIds.DetailsHeading(),
                        children: hasIncludedServices ? reviewTexts.bundleDetails() : reviewTexts.serviceDetails()
                    }),
                    /*#__PURE__*/ _jsx(RichTextViewer, {
                        value: service.description,
                        skin: "standard"
                    })
                ]
            }),
            hasIncludedServices && !hideIncludedServices && /*#__PURE__*/ _jsxs(Column, {
                gap: 4,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        weight: "bold",
                        skin: "standard",
                        testId: testIds.IncludedServicesHeading(),
                        children: reviewTexts.includedServicesLabel()
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: styles.detailsList,
                        children: includedServices.map((bundleService, idx)=>/*#__PURE__*/ _jsx("li", {
                                children: /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    skin: "standard",
                                    weight: "light",
                                    testId: testIds.IncludedServiceName(idx),
                                    children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                                        text: bundleService.name,
                                        resolvedValues: resolvedValues
                                    })
                                })
                            }, bundleService.serviceTemplateId))
                    })
                ]
            }),
            includedWithDescription.length > 0 && /*#__PURE__*/ _jsx(Column, {
                gap: 8,
                testId: includedServiceDescriptionsTestId,
                children: includedWithDescription.map((bundleService)=>/*#__PURE__*/ _jsxs(Column, {
                        gap: 4,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                weight: "bold",
                                skin: "standard",
                                children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                                    text: bundleService.name,
                                    resolvedValues: resolvedValues
                                })
                            }),
                            /*#__PURE__*/ _jsx(RichTextViewer, {
                                value: bundleService.description,
                                skin: "standard"
                            })
                        ]
                    }, bundleService.serviceTemplateId))
            }),
            bulletPoints.bullets.length > 0 && /*#__PURE__*/ _jsxs(Column, {
                gap: 4,
                testId: billingDetailsTestId,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        weight: "bold",
                        skin: "standard",
                        children: reviewTexts.billing()
                    }),
                    bulletPoints.bullets.map((bullet, i)=>{
                        if (typeof bullet === 'string') {
                            return /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                skin: "standard",
                                weight: "light",
                                children: bullet
                            }, i);
                        }
                        if ('indented' in bullet) {
                            return /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                skin: "standard",
                                weight: "light",
                                children: bullet.text
                            }, i);
                        }
                        return /*#__PURE__*/ _jsx(Box, {
                            children: bullet
                        }, i);
                    })
                ]
            })
        ]
    });
};
